import type {
  LightboxAsset,
  LightboxMediaItem,
  LightboxVideoProvider,
} from './interfaces.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'

const IMAGE_ASSET_TYPES = new Set(['product_image', 'additional_product_image'])
const CMS_VIDEO_PATH = '/static/uploads-cms2/'

const VIDEO_PROVIDERS = new Set<LightboxVideoProvider>(['cms', 'youtube', 'vimeo'])

function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&autoplay=1&playsinline=1`
}

function youtubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}

function vimeoEmbedUrl(videoId: string): string {
  return `https://player.vimeo.com/video/${videoId}?autoplay=1&playsinline=1`
}

function resolveCmsVideoSrc(filename: string): string {
  if (filename.startsWith('http://') || filename.startsWith('https://') || filename.startsWith('/')) {
    return filename
  }
  return `${CMS_VIDEO_PATH}${filename}`
}

function normalizeVideoProvider(provider: string | null): LightboxVideoProvider | null {
  const normalized = provider?.toLowerCase()
  if (normalized && VIDEO_PROVIDERS.has(normalized as LightboxVideoProvider)) {
    return normalized as LightboxVideoProvider
  }
  return null
}

function mapImage(source: string, alt: string, fallbackSource: string): LightboxMediaItem {
  const item: LightboxMediaItem = {
    kind: 'image',
    source: source || fallbackSource,
    alt,
  }
  if (fallbackSource) {
    item.fallbackSource = fallbackSource
  }
  return item
}

function withThumbnailSource(item: LightboxMediaItem, thumbnailSource?: string | null): LightboxMediaItem {
  return thumbnailSource ? { ...item, thumbnailSource } : item
}

function mapImageAsset(asset: LightboxAsset, fallbackSource: string): LightboxMediaItem {
  return mapImage(asset.asset, asset.name || asset.asset, fallbackSource)
}

function mapVideoAsset(asset: LightboxAsset): LightboxMediaItem | null {
  return mapVideo(asset.video_provider, asset.asset, asset.name || asset.asset)
}

function mapVideo(videoProvider: string | null, videoId: string, alt: string): LightboxMediaItem | null {
  const provider = normalizeVideoProvider(videoProvider)
  if (!provider) {
    return null
  }

  switch (provider) {
    case 'youtube':
      return {
        kind: 'video',
        source: youtubeThumbnailUrl(videoId),
        alt,
        videoProvider: provider,
        embedSrc: youtubeEmbedUrl(videoId),
      }
    case 'vimeo':
      return {
        kind: 'video',
        source: '',
        alt,
        videoProvider: provider,
        embedSrc: vimeoEmbedUrl(videoId),
      }
    case 'cms':
      return {
        kind: 'video',
        source: '',
        alt,
        videoProvider: provider,
        sources: [{ src: resolveCmsVideoSrc(videoId), type: 'video/mp4' }],
      }
    default:
      return null
  }
}

export function mapLightboxAssets(assets: LightboxAsset[], fallbackSource = ''): LightboxMediaItem[] {
  return assets
    .map((asset) => {
      if (IMAGE_ASSET_TYPES.has(asset.type)) {
        return mapImageAsset(asset, fallbackSource)
      }
      if (asset.type === 'video') {
        return mapVideoAsset(asset)
      }
      return null
    })
    .filter((item): item is LightboxMediaItem => item !== null)
}

/** Lightbox media of the product loaded by csc-pdp-provider: main picture, extra pictures, then videos */
export function mapPdpProductToLightboxMediaItems(product: PdpProduct, fallbackSource = ''): LightboxMediaItem[] {
  const images = [
    withThumbnailSource(mapImage(product.picture?.large ?? '', product.name, fallbackSource), product.picture?.mini),
    ...(product.extra_pictures ?? [])
      .filter(picture => picture.large)
      .map(picture => withThumbnailSource(mapImage(picture.large ?? '', picture.alt_text || product.name, fallbackSource), picture.mini)),
  ]

  const videos = (product.assets ?? [])
    .filter(asset => asset.type === 'video' && asset.asset)
    .map(asset => mapVideo(asset.video_provider ?? null, asset.asset ?? '', asset.name || product.name))
    .filter((item): item is LightboxMediaItem => item !== null)

  return [...images, ...videos]
}

export function normalizeLightboxMediaItem(item: LightboxMediaItem): LightboxMediaItem {
  if (item.kind) {
    return item
  }
  return { ...item, kind: 'image' }
}

export function mapLightboxImages(items: LightboxMediaItem[]): LightboxMediaItem[] {
  return items.map(normalizeLightboxMediaItem)
}

export async function fetchVimeoThumbnail(videoId: string): Promise<string> {
  try {
    const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`)
    if (!response.ok) {
      return ''
    }
    const data = await response.json() as Array<{ thumbnail_large?: string }>
    return data[0]?.thumbnail_large ?? ''
  } catch {
    return ''
  }
}

export function extractVimeoVideoId(embedSrc?: string): string {
  if (!embedSrc) {
    return ''
  }
  const match = embedSrc.match(/vimeo\.com\/video\/(\d+)/)
  return match?.[1] ?? ''
}

export async function resolveVimeoThumbnails(items: LightboxMediaItem[]): Promise<LightboxMediaItem[]> {
  const resolved = await Promise.all(items.map(async (item) => {
    if (item.kind !== 'video' || item.videoProvider !== 'vimeo' || item.source) {
      return item
    }

    const videoId = extractVimeoVideoId(item.embedSrc)
    if (!videoId) {
      return item
    }

    const thumbnail = await fetchVimeoThumbnail(videoId)
    return thumbnail ? { ...item, source: thumbnail } : item
  }))

  return resolved
}
